/**
 * dropSubtreeLayout — turn a `CompassSubtreeDragPayload` into a set of
 * pre-positioned frames, shapes, and edges, ready for the canvas drop
 * handler to instantiate as commands.
 *
 * Layout strategy (kept intentionally simple; the full
 * `InfraLayoutEngine` is reserved for overview rendering where
 * clustering collapses resources — not desirable when a user
 * explicitly dropped a branch onto canvas expecting every leaf to
 * materialise):
 *
 *   Wrapper frame (branch label)
 *     └─ Container frames (VPC)
 *          └─ Sub-container frames (subnet)
 *               └─ Resource shapes in a grid
 *
 * Resources without a known container still materialise inside the
 * wrapper frame under an "Unassigned" row so nothing is dropped on
 * the floor. Edges between dropped resources are emitted with the
 * pre-assigned shape ids so connectors land at the right endpoints.
 *
 * Pure function — unit-testable, no store / DOM / React references.
 */

import type { CompassSubtreeDragPayload } from '../components/panel/CompassFolderResourcesSection';

const SHAPE_W = 180;
const SHAPE_H = 70;
const GRID_GAP_X = 16;
const GRID_GAP_Y = 16;
const FRAME_PAD_X = 16;
const FRAME_PAD_Y = 36; // header space
const FRAME_GAP = 20;
const COLS = 4;

export interface PlannedShape {
  readonly newId: string;
  readonly sourceId: string;
  readonly manifestId: string;
  readonly label: string;
  readonly props: Record<string, unknown>;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /**
   * Deepest container frame id that encloses this shape. Used by the
   * drop handler to stamp `attrs.compassParentId` so the container
   * drag-sync can cascade moves to children.
   */
  parentFrameId?: string;
}

export interface PlannedFrame {
  readonly id: string;
  readonly kind: 'wrapper' | 'vpc' | 'subnet' | 'account' | 'other';
  readonly label: string;
  readonly x: number;
  readonly y: number;
  width: number;
  height: number;
  /** Enclosing parent frame id — undefined for root frames. */
  parentFrameId?: string;
}

export interface PlannedEdge {
  readonly id: string;
  readonly fromShapeId: string;
  readonly toShapeId: string;
  readonly type: string;
}

export interface DropLayoutResult {
  readonly frames: PlannedFrame[];
  readonly shapes: PlannedShape[];
  readonly edges: PlannedEdge[];
  readonly width: number;
  readonly height: number;
}

type ContainerKind = 'vpc' | 'subnet' | 'account' | 'other';

interface ContainerNode {
  readonly id: string;
  readonly kind: ContainerKind;
  readonly label: string;
  readonly parentId?: string;
  readonly children: ContainerNode[];
  readonly resources: CompassSubtreeDragPayload['resources'][number][];
}

/**
 * Main entry. Returns positions relative to (0, 0); caller adds the
 * drop point offset when issuing commands.
 *
 * Wrapper dedup: when the branch the user dragged was itself a single
 * container (e.g. a VPC) and the payload's root container has the
 * matching kind, we skip the outer wrapper frame — the root container
 * already serves that role. Prevents the "two VPCs nested" artifact.
 */
export function layoutDroppedSubtree(
  payload: CompassSubtreeDragPayload,
  idFactory: () => string,
): DropLayoutResult {
  // 1. Build container tree from the payload's containers.
  const byId = new Map<string, ContainerNode>();
  for (const c of payload.containers) {
    byId.set(c.id, {
      id: c.id,
      kind: c.kind,
      label: c.label,
      parentId: c.parentId,
      children: [],
      resources: [],
    });
  }
  const roots: ContainerNode[] = [];
  for (const n of byId.values()) {
    if (n.parentId && byId.has(n.parentId)) {
      byId.get(n.parentId)!.children.push(n);
    } else {
      roots.push(n);
    }
  }

  // 2. Bucket resources by their nearest container. Unassigned = no
  //    container or container not in the payload tree.
  const unassigned: CompassSubtreeDragPayload['resources'][number][] = [];
  for (const r of payload.resources) {
    const container = r.containerId ? byId.get(r.containerId) : undefined;
    if (container) container.resources.push(r);
    else unassigned.push(r);
  }

  // 3. Plan layout recursively for each root container and for the
  //    unassigned bucket.
  const shapes: PlannedShape[] = [];
  const frames: PlannedFrame[] = [];
  const edges: PlannedEdge[] = [];
  const shapeIdBySource = new Map<string, string>();

  let cursorY = FRAME_PAD_Y;
  const innerX = FRAME_PAD_X;
  let maxInnerWidth = 0;

  for (const root of roots) {
    const planned = planContainer(root, innerX, cursorY, shapes, shapeIdBySource, frames, idFactory);
    cursorY = planned.bottom + FRAME_GAP;
    maxInnerWidth = Math.max(maxInnerWidth, planned.right - innerX);
  }

  // Track parent linkage so children can be made to drag with their
  // container after the drop. We derive parentage from the frame
  // tree: each shape/frame picks the deepest frame that strictly
  // encloses it as its parent (sorted by area ascending).
  const framesSorted = [...frames].sort((a, b) => a.width * a.height - b.width * b.height);
  for (const shape of shapes) {
    for (const f of framesSorted) {
      if (
        shape.x >= f.x &&
        shape.x < f.x + f.width &&
        shape.y >= f.y &&
        shape.y < f.y + f.height
      ) {
        shape.parentFrameId = f.id;
        break;
      }
    }
  }
  for (const fr of frames) {
    for (const candidate of framesSorted) {
      if (candidate.id === fr.id) continue;
      if (candidate.width * candidate.height <= fr.width * fr.height) continue;
      if (
        fr.x >= candidate.x &&
        fr.x < candidate.x + candidate.width &&
        fr.y >= candidate.y &&
        fr.y < candidate.y + candidate.height
      ) {
        fr.parentFrameId = candidate.id;
        break;
      }
    }
  }

  if (unassigned.length > 0) {
    const planned = planResourceGrid(unassigned, innerX, cursorY, shapes, shapeIdBySource, idFactory);
    // Treat unassigned as a row; no frame wrapping. Adjust width.
    cursorY = planned.bottom + FRAME_GAP;
    maxInnerWidth = Math.max(maxInnerWidth, planned.right - innerX);
  }

  // 4. Wrap everything in a single "branch" frame — unless the drop
  //    already emitted a single root frame whose kind matches the
  //    branch kind. Without this, dragging a VPC produces a wrapper
  //    VPC containing the same-named VPC container.
  const rootFrames = frames.filter((f) => f.x === innerX || f.x === 0);
  const wrapperKindMap: Record<string, ContainerKind> = {
    vpc: 'vpc',
    subnet: 'subnet',
    account: 'account',
  };
  const wrapperKindFromBranch = wrapperKindMap[payload.branchKind];
  const shouldSkipWrapper =
    wrapperKindFromBranch &&
    rootFrames.length === 1 &&
    rootFrames[0].kind === wrapperKindFromBranch &&
    rootFrames[0].label === payload.branchLabel;

  let wrapperWidth: number;
  let wrapperHeight: number;
  if (shouldSkipWrapper) {
    wrapperWidth = rootFrames[0].width;
    wrapperHeight = rootFrames[0].height;
  } else {
    wrapperWidth = Math.max(
      maxInnerWidth + FRAME_PAD_X * 2,
      SHAPE_W * COLS + GRID_GAP_X * (COLS - 1) + FRAME_PAD_X * 2,
    );
    wrapperHeight = cursorY + FRAME_PAD_Y;
    const wrapperFrame: PlannedFrame = {
      id: idFactory(),
      kind: 'wrapper',
      label: payload.branchLabel,
      x: 0,
      y: 0,
      width: wrapperWidth,
      height: wrapperHeight,
    };
    frames.unshift(wrapperFrame);
  }

  // 5. Edges — remap source node ids to the newly-generated shape ids.
  for (const e of payload.edges) {
    const fromShapeId = shapeIdBySource.get(e.from);
    const toShapeId = shapeIdBySource.get(e.to);
    if (!fromShapeId || !toShapeId) continue;
    edges.push({
      id: idFactory(),
      fromShapeId,
      toShapeId,
      type: e.type,
    });
  }

  return {
    frames,
    shapes,
    edges,
    width: wrapperWidth,
    height: wrapperHeight,
  };
}

function planContainer(
  container: ContainerNode,
  originX: number,
  originY: number,
  shapes: PlannedShape[],
  shapeIdBySource: Map<string, string>,
  frames: PlannedFrame[],
  idFactory: () => string,
): { right: number; bottom: number; frameId: string | null } {
  const innerX = originX + FRAME_PAD_X;
  let cursorY = originY + FRAME_PAD_Y;
  let maxRight = innerX + SHAPE_W;

  // Reserve this container's frame position FIRST — we know its
  // top-left (originX, originY) but not its full size until children
  // lay out. Placeholder object is mutated once we know the bounds.
  // Pushing before recursion guarantees parents land at lower z than
  // their children so subnets render inside VPCs and resources render
  // on top of subnets. (Konva renders in insertion order.)
  const hasContent = container.children.length > 0 || container.resources.length > 0;
  const frameId = hasContent ? idFactory() : null;
  const placeholder: PlannedFrame | null = frameId
    ? {
        id: frameId,
        kind: container.kind,
        label: container.label,
        x: originX,
        y: originY,
        width: 0,
        height: 0,
      }
    : null;
  if (placeholder) frames.push(placeholder);

  // Children after self so they sit on top.
  for (const child of container.children) {
    const planned = planContainer(child, innerX, cursorY, shapes, shapeIdBySource, frames, idFactory);
    cursorY = planned.bottom + FRAME_GAP;
    maxRight = Math.max(maxRight, planned.right);
  }

  // Direct resources grid — these are on top of the container.
  if (container.resources.length > 0) {
    const grid = planResourceGrid(
      container.resources,
      innerX,
      cursorY,
      shapes,
      shapeIdBySource,
      idFactory,
    );
    cursorY = grid.bottom;
    maxRight = Math.max(maxRight, grid.right);
  }

  const right = maxRight + FRAME_PAD_X;
  const bottom = cursorY + FRAME_PAD_Y;

  // Backfill the placeholder's final dimensions.
  if (placeholder) {
    placeholder.width = right - originX;
    placeholder.height = bottom - originY;
  }

  return { right, bottom, frameId };
}

function planResourceGrid(
  resources: readonly CompassSubtreeDragPayload['resources'][number][],
  originX: number,
  originY: number,
  shapes: PlannedShape[],
  shapeIdBySource: Map<string, string>,
  idFactory: () => string,
): { right: number; bottom: number } {
  let right = originX + SHAPE_W;
  let bottom = originY + SHAPE_H;
  resources.forEach((res, i) => {
    const col = i % COLS;
    const row = Math.floor(i / COLS);
    const x = originX + col * (SHAPE_W + GRID_GAP_X);
    const y = originY + row * (SHAPE_H + GRID_GAP_Y);
    const id = idFactory();
    shapeIdBySource.set(res.id, id);
    shapes.push({
      newId: id,
      sourceId: res.id,
      manifestId: res.manifestId,
      label: res.label,
      props: res.props,
      x,
      y,
      width: SHAPE_W,
      height: SHAPE_H,
    });
    right = Math.max(right, x + SHAPE_W);
    bottom = Math.max(bottom, y + SHAPE_H);
  });
  return { right, bottom };
}
